/**
 * Admin extensions — Team / Waitlist / Partners / Vouchers / Raffle / Inbox.
 *
 * These pages are exported from this file and routed via /admin/* in App.js.
 * They follow the same visual language as the rest of Admin.jsx (border-2,
 * monospaced overlines, mango accents) but live in their own module to keep
 * Admin.jsx manageable.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import HelpHint from "../components/HelpHint";

// =====================================================================
//                              SECTORS CMS
// =====================================================================
export function AdminSectors() {
  const [rows, setRows] = useState([]);
  const blank = { key: "", label: "", label_ur: "", tagline: "", description: "", color: "", icon: "", hero_image_url: "", display_order: 100, visible_on_homepage: true, active: true, seo_title: "", seo_description: "", seo_intro_paragraph: "" };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/sectors").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, display_order: Number(form.display_order) };
      if (editing) await api.put(`/admin/sectors/${editing}`, body);
      else await api.post("/admin/sectors", body);
      toast.success("Saved"); setEditing(null);
      setForm(blank);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({
      key: s.key, label: s.label, label_ur: s.label_ur || "", tagline: s.tagline || "", description: s.description || "",
      color: s.color || "", icon: s.icon || "", hero_image_url: s.hero_image_url || "",
      seo_title: s.seo_title || "", seo_description: s.seo_description || "", seo_intro_paragraph: s.seo_intro_paragraph || "",
      display_order: s.display_order || 100,
      visible_on_homepage: s.visible_on_homepage !== false, active: s.active !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="max-w-6xl" data-testid="admin-sectors">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">SECTION · SECTORS</div>
        <h1 className="text-4xl font-black tracking-tighter">Sectors & area pages</h1>
        <p className="text-sm text-neutral-500 mt-1">Edit the sectors that appear in the public nav and as blog-style area pages. <b>Key</b> is the URL slug ({"/sectors/{key}"}) and cannot conflict.</p>
      </div>

      <form onSubmit={save} className="bg-white border-2 border-black p-5 mb-8 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sector-form">
        <div><Label className="text-xs">Key (URL slug)</Label><Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="entertainment" required disabled={!!editing} /></div>
        <div><Label className="text-xs">Label (shown in nav)</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Entertainment" required /></div>
        <div><Label className="text-xs">Label — Urdu (shown when a patron switches to اردو; leave blank to fall back to the English label above)</Label><Input dir="rtl" lang="ur" value={form.label_ur} onChange={(e) => setForm({ ...form, label_ur: e.target.value })} placeholder="تفریح" className="text-lg py-5" style={{ fontFamily: "'Noto Naskh Arabic', system-ui, sans-serif" }} /></div>
        <div className="md:col-span-2"><Label className="text-xs">Tagline (one-liner shown on the area page)</Label><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Film, music, streaming and celebrity." /></div>
        <div className="md:col-span-2"><Label className="text-xs">Description (long copy for the area page hero)</Label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-neutral-300 p-2 text-sm" rows={2} /></div>
        <div><Label className="text-xs">Color (hex)</Label><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="#a855f7" /></div>
        <div><Label className="text-xs">Icon (emoji)</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="🎬" /></div>
        <div><Label className="text-xs flex items-center gap-1">Hero image URL <HelpHint text="Recommended: 1200×630px (standard OG ratio). Not shown inline on the sector page itself — used only as the social-share preview image when this sector's page (/sectors/{key}) is shared on WhatsApp, Twitter, Facebook, etc." /></Label><Input value={form.hero_image_url} onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })} /></div>
        <div><Label className="text-xs">Display order</Label><Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} /></div>
        <div className="flex items-center gap-3"><Switch checked={form.visible_on_homepage} onCheckedChange={(v) => setForm({ ...form, visible_on_homepage: v })} /><span className="text-sm">Visible in homepage menu</span></div>
        <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><span className="text-sm">Active</span></div>
        <div className="md:col-span-2 mt-2 pt-3 border-t-2 border-dashed border-neutral-300">
          <div className="overline mb-2 text-mango text-[11px]">SEO OVERRIDES FOR /sectors/{form.key || "<key>"}</div>
          <p className="text-xs text-neutral-500 mb-3">Leave any field blank to inherit the site-wide defaults from Site Content → SEO. Filled fields take precedence on this specific sector page.</p>
        </div>
        <div className="md:col-span-2"><Label className="text-xs">SEO &lt;title&gt; — appears in browser tabs &amp; Google results (60-65 chars ideal)</Label><Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} placeholder={`${form.label || "Sector"} — Pakistan consumer insights | oodel.`} /></div>
        <div className="md:col-span-2"><Label className="text-xs">SEO meta description (150-160 chars ideal)</Label><textarea value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className="w-full border border-neutral-300 p-2 text-sm" rows={2} placeholder={`Track ${(form.label || "this sector").toLowerCase()} sentiment, brand health, and consumer trends across Pakistan.`} /></div>
        <div className="md:col-span-2"><Label className="text-xs">SEO intro paragraph — rendered as a highlighted block below the hero. Use for evergreen, keyword-rich copy.</Label><textarea value={form.seo_intro_paragraph} onChange={(e) => setForm({ ...form, seo_intro_paragraph: e.target.value })} className="w-full border border-neutral-300 p-2 text-sm" rows={3} placeholder={`${form.label || "This sector"} in Pakistan is shaped by ... (write 2-3 sentences for search ranking)`} /></div>
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" className="bg-black hover:bg-neutral-800 rounded-none">{editing ? "Update" : "Add"} sector</Button>
          {editing && <Button type="button" variant="outline" onClick={() => { setEditing(null); setForm(blank); }}>Cancel</Button>}
        </div>
      </form>

      <div className="space-y-2">
        {rows.map((s) => (
          <div key={s.id} className="bg-white border border-neutral-200 p-3 flex items-center justify-between" data-testid={`sector-row-${s.key}`}>
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <span className="text-2xl" aria-hidden="true">{s.icon}</span>
              <div className="min-w-0">
                <div className="font-medium">{s.label} <span className="text-neutral-500 text-xs font-mono">/{s.key}</span></div>
                <div className="text-xs text-neutral-500 truncate">{s.tagline}</div>
              </div>
              {!s.active && <span className="text-xs bg-neutral-200 px-2 py-0.5">Inactive</span>}
              {!s.visible_on_homepage && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5">Hidden</span>}
            </div>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => startEdit(s)}>Edit</Button>
              <Button variant="outline" size="sm" onClick={async () => { if (window.confirm(`Delete ${s.label}?`)) { await api.delete(`/admin/sectors/${s.id}`); load(); } }}>Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
//                  PM ASSIGNMENT WIDGET (used in customer detail)
// =====================================================================
export function PMAssignmentWidget({ customerId, currentIds = [], onChange, endpoint = null }) {
  const [team, setTeam] = useState([]);
  const [selected, setSelected] = useState(currentIds);
  const assignUrl = endpoint || `/admin/customers/${customerId}/assign-pms`;
  useEffect(() => { api.get("/admin/team").then((r) => setTeam(r.data.filter((t) => ["admin", "pm"].includes(t.role)))); }, []);
  useEffect(() => { setSelected(currentIds || []); }, [currentIds.join(",")]);

  const toggle = (id) => {
    setSelected((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const save = async () => {
    try {
      await api.put(assignUrl, { account_manager_ids: selected });
      toast.success("Assignment saved");
      onChange && onChange(selected);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-white border-2 border-black p-4" data-testid="pm-assignment">
      <div className="overline mb-2">ASSIGNED PMS</div>
      <div className="space-y-1 max-h-64 overflow-auto mb-3">
        {team.map((t) => (
          <label key={t.id} className="flex items-center gap-2 text-sm p-1 cursor-pointer hover:bg-neutral-50">
            <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggle(t.id)} data-testid={`pm-check-${t.id}`} />
            <span className="flex-1">{t.name}</span>
            <span className="text-xs text-neutral-500 uppercase">{t.role}</span>
          </label>
        ))}
        {team.length === 0 && <div className="text-xs text-neutral-500 italic">No PMs yet — invite from Team.</div>}
      </div>
      <Button onClick={save} size="sm" className="bg-mango hover:bg-mango/90 text-white w-full" data-testid="pm-assign-save">Save assignment</Button>
    </div>
  );
}

// =====================================================================
//                              TEAM
// =====================================================================
export function AdminTeam() {
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "pm", phone: "" });
  const [removeTarget, setRemoveTarget] = useState(null); // { id, name, email, role, is_growth_lead }
  const [removeBusy, setRemoveBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/team").then((r) => setTeam(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/admin/team", form);
      if (r.data.promoted) {
        toast.success(
          r.data.was_existing_patron
            ? `Existing patron promoted to ${r.data.role} — notification sent.`
            : `Role updated to ${r.data.role}.`,
        );
      } else {
        toast.success(`Invited — temp password: ${r.data.temp_password}`);
      }
      setForm({ email: "", name: "", role: "pm", phone: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Invite failed"); }
  };

  const updateRole = async (uid, role) => {
    await api.put(`/admin/team/${uid}/role`, { role });
    load();
  };

  const resetPw = async (uid) => {
    const r = await api.post(`/admin/team/${uid}/reset-password`);
    toast.success(`Temp password: ${r.data.temp_password}`);
  };

  const doRemove = async (mode) => {
    if (!removeTarget) return;
    setRemoveBusy(true);
    try {
      await api.delete(`/admin/team/${removeTarget.id}?mode=${mode}`);
      toast.success(mode === "delete"
        ? `${removeTarget.name} deleted permanently.`
        : `${removeTarget.name} demoted to Patron — account remains active.`);
      setRemoveTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setRemoveBusy(false);
    }
  };

  return (
    <div className="max-w-6xl" data-testid="admin-team">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">SECTION · TEAM</div>
        <h1 className="text-4xl font-black tracking-tighter">Team members</h1>
        <p className="text-sm text-neutral-500 mt-1">Admins have full access. PMs see only their assigned business clients + the Inbox. Researchers manage questions, trackers and editorial. Growth Leads are volunteer team members with their own dashboard + referral link — they can also take surveys as normal panelists.</p>
      </div>

      <form onSubmit={invite} className="bg-white border-2 border-black p-5 mb-8 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="team-invite-form">
        <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="team-name" />
        <Input type="email" placeholder="email@oodel.co" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="team-email" />
        <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="team-phone" />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-neutral-300 px-3 py-2 bg-white" data-testid="team-role">
          <option value="admin">Admin</option>
          <option value="pm">Project Manager</option>
          <option value="researcher">Researcher</option>
          <option value="growth_lead">Growth Lead</option>
        </select>
        <Button type="submit" className="bg-black hover:bg-neutral-800 rounded-none" data-testid="team-invite">Invite</Button>
      </form>

      <div className="space-y-2">
        {team.map((m) => (
          <div key={m.id} className="bg-white border border-neutral-200 p-4 flex items-center justify-between gap-3" data-testid={`team-row-${m.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-medium">{m.name}</div>
              <div className="text-xs text-neutral-500">{m.email}{m.phone_number ? ` · ${m.phone_number}` : ""}</div>
            </div>
            <select value={m.is_growth_lead ? "growth_lead" : m.role} onChange={(e) => updateRole(m.id, e.target.value)} className="border border-neutral-300 px-2 py-1 text-sm" data-testid={`team-role-${m.id}`}>
              <option value="admin">Admin</option>
              <option value="pm">PM</option>
              <option value="researcher">Researcher</option>
              <option value="growth_lead">Growth Lead</option>
            </select>
            <Button variant="outline" size="sm" onClick={() => resetPw(m.id)}>Reset password</Button>
            <Button variant="outline" size="sm" onClick={() => setRemoveTarget(m)} data-testid={`team-remove-${m.id}`}>Remove…</Button>
          </div>
        ))}
        {!loading && team.length === 0 && <div className="text-neutral-500 italic">No team members yet — invite your first PM, Researcher, or Growth Lead.</div>}
      </div>

      <Dialog open={!!removeTarget} onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}>
        <DialogContent className="rounded-none border-2 border-black max-w-md" data-testid="team-remove-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black tracking-tighter">
              Remove {removeTarget?.name}?
            </DialogTitle>
            <DialogDescription className="text-neutral-700 pt-1">
              Pick one — both actions terminate any Growth Lead referral link the user had.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <button
              type="button"
              disabled={removeBusy}
              onClick={() => doRemove("demote")}
              data-testid="team-remove-demote"
              className="w-full text-left border-2 border-black bg-white hover:bg-mango/20 p-4 disabled:opacity-50"
            >
              <div className="font-bold">Demote to Patron</div>
              <div className="text-xs text-neutral-600 mt-1">
                Strips the team title (PM / Researcher / Growth Lead) but keeps the account active.
                They can still log in and take surveys as a normal panelist. Points and history preserved.
              </div>
            </button>
            <button
              type="button"
              disabled={removeBusy}
              onClick={() => doRemove("delete")}
              data-testid="team-remove-delete"
              className="w-full text-left border-2 border-red-600 bg-white hover:bg-red-50 p-4 disabled:opacity-50"
            >
              <div className="font-bold text-red-700">Delete permanently</div>
              <div className="text-xs text-neutral-600 mt-1">
                Hard-deletes the user record. They can no longer log in.
                Aggregate survey responses are retained for research integrity.
              </div>
            </button>
          </div>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setRemoveTarget(null)} disabled={removeBusy} data-testid="team-remove-cancel">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =====================================================================
//                            WAITLIST
// =====================================================================
export function AdminWaitlist() {
  const [allRows, setAllRows] = useState([]);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onlyUnconverted, setOnlyUnconverted] = useState(true);
  const [includeUnconfirmed, setIncludeUnconfirmed] = useState(false);
  const [selected, setSelected] = useState(new Set());
  // Tab: 'pakistan' or 'overseas'. Overseas tab surfaces every signup whose
  // detected_country was non-PK at signup time — admin notifies them with a
  // separate template once overseas surveys go live.
  const [tab, setTab] = useState("pakistan");
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/waitlist/admin/list").then((r) => { setAllRows(r.data); setSelected(new Set()); }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  // Filter rows by active tab. A row is Overseas if is_overseas=true OR (legacy
  // fallback) its geo.country_code is set and not PK.
  const isOverseasRow = (r) => {
    if (r.is_overseas === true) return true;
    if (r.is_overseas === false) return false;
    const cc = (r.geo?.country_code || "").toUpperCase();
    return cc && cc !== "PK";
  };
  const rows = allRows.filter((r) => (tab === "overseas" ? isOverseasRow(r) : !isOverseasRow(r)));
  const pkCount = allRows.filter((r) => !isOverseasRow(r)).length;
  const overseasCount = allRows.filter((r) => isOverseasRow(r)).length;

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const openDraft = async (mode) => {
    try {
      const r = await api.get("/waitlist/admin/notify-draft");
      setDraft({ ...r.data, mode });  // mode = 'all' | 'selected'
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to draft email"); }
  };

  const send = async () => {
    setBusy(true);
    try {
      const payload = {
        subject: draft.subject, body_html: draft.body_html, only_unconverted: onlyUnconverted,
        // Pass the active tab so backend can scope the broadcast properly + we
        // can later swap the template if `tab === "overseas"`.
        scope: tab,
      };
      if (draft.mode === "selected") {
        payload.recipient_ids = Array.from(selected);
      } else {
        payload.include_unconfirmed = includeUnconfirmed;
        // For "Notify All" within Overseas tab, restrict to overseas IDs so
        // backend doesn't accidentally email PK signups.
        if (tab === "overseas") payload.recipient_ids = rows.map((r) => r.id);
      }
      const r = await api.post("/waitlist/admin/notify", payload);
      if (r.data.queued === 0) {
        toast.warning("0 emails queued — no recipients matched your filter. Try enabling 'Include unconfirmed' or selecting rows directly.");
      } else {
        toast.success(`Queued ${r.data.queued} emails (outbox).`);
      }
      setDraft(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  const deleteRow = async (id, email) => {
    if (!window.confirm(`Permanently delete ${email} from the waitlist?`)) return;
    try {
      await api.delete(`/waitlist/admin/${id}`);
      toast.success("Signup deleted.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const exportCsv = () => {
    const head = "Email,Name,Source,Confirmed,Converted,Founding,FP#,City,Country,IP,Created\n";
    const body = rows.map((r) => [
      r.email, r.name, r.source || "", r.confirmed_at || "", r.converted_to_patron_at || "",
      r.is_founding_patron ? "yes" : "no", r.founding_patron_number ?? "",
      r.geo?.city || "", r.geo?.country || "", r.ip || "", r.created_at,
    ].map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([head + body], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "waitlist.csv"; a.click();
  };

  const confirmed = rows.filter((r) => r.confirmed_at).length;
  const converted = rows.filter((r) => r.converted_to_patron_at).length;
  const allChecked = selected.size === rows.length && rows.length > 0;

  return (
    <div className="max-w-6xl" data-testid="admin-waitlist">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">SECTION · WAITLIST</div>
        <h1 className="text-4xl font-black tracking-tighter">Founding Patron pool</h1>
        <p className="text-sm text-neutral-500 mt-1">When you launch a wave, the system auto-emails every confirmed patron. Use <b>Notify Patrons</b> for an ad-hoc broadcast (e.g. teaser before launch). Pick specific rows below to notify just them.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Total signups" value={rows.length} />
        <Stat label="Confirmed" value={confirmed} />
        <Stat label="Converted to Patron" value={converted} />
        <Stat label="Conversion %" value={`${rows.length ? Math.round(converted / rows.length * 100) : 0}%`} />
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <Button onClick={() => openDraft("all")} className="bg-mango hover:bg-mango/90 text-white rounded-none" data-testid="notify-draft">
          {tab === "overseas" ? "Notify Overseas →" : "Notify All →"}
        </Button>
        <Button onClick={() => openDraft("selected")} disabled={selected.size === 0} className="bg-black hover:bg-neutral-800 text-white rounded-none disabled:opacity-40" data-testid="notify-selected">
          Notify Selected ({selected.size}) →
        </Button>
        <Button variant="outline" onClick={exportCsv} className="rounded-none" data-testid="export-csv">Export CSV</Button>
        {rows.length > 0 && (
          <label className="flex items-center gap-2 text-xs text-neutral-600 ml-auto cursor-pointer">
            <input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="select-all" />
            Select all ({rows.length})
          </label>
        )}
      </div>

      {/* Tabs — Pakistan vs Overseas. Counts always reflect total dataset, not
          the currently-rendered subset. */}
      <div className="flex gap-px bg-black border-2 border-black mb-3" data-testid="waitlist-tabs">
        <button
          onClick={() => { setTab("pakistan"); setSelected(new Set()); }}
          data-testid="waitlist-tab-pakistan"
          className={`px-4 py-2 text-sm font-bold ${tab === "pakistan" ? "bg-mango text-black" : "bg-white text-neutral-700 hover:bg-neutral-100"}`}
        >
          Pakistan <span className={`ml-2 text-[10px] font-mono px-1.5 py-0.5 ${tab === "pakistan" ? "bg-black text-mango" : "bg-neutral-200"}`}>{pkCount}</span>
        </button>
        <button
          onClick={() => { setTab("overseas"); setSelected(new Set()); }}
          data-testid="waitlist-tab-overseas"
          className={`px-4 py-2 text-sm font-bold ${tab === "overseas" ? "bg-mango text-black" : "bg-white text-neutral-700 hover:bg-neutral-100"}`}
        >
          Overseas <span className={`ml-2 text-[10px] font-mono px-1.5 py-0.5 ${tab === "overseas" ? "bg-black text-mango" : "bg-neutral-200"}`}>{overseasCount}</span>
        </button>
      </div>

      <div className="space-y-1">
        {rows.map((r) => {
          const geo = r.geo || {};
          const where = [geo.city, geo.country].filter(Boolean).join(", ");
          return (
          <div key={r.id} className="bg-white border border-neutral-200 p-3 flex items-center justify-between gap-3 text-sm">
            <input
              type="checkbox"
              checked={selected.has(r.id)}
              onChange={() => toggle(r.id)}
              className="shrink-0"
              data-testid={`select-${r.id}`}
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{r.name} <span className="text-neutral-500">· {r.email}</span></div>
              <div className="text-xs text-neutral-500">
                {r.source || "homepage"} · {r.created_at?.slice(0, 10)}
                {where && <> · <span title={`IP ${r.ip}`} className="text-neutral-700">📍 {where}</span></>}
                {!where && r.ip && <> · <span className="text-neutral-400" title="IP geolocation unavailable">📍 {r.ip}</span></>}
              </div>
            </div>
            <div className="flex gap-2 text-xs items-center">
              {r.is_founding_patron && <span className="bg-mango/10 text-mango px-2 py-0.5 border border-mango">✦ #{r.founding_patron_number}</span>}
              {r.confirmed_at ? <span className="bg-green-100 text-green-800 px-2 py-0.5">Confirmed</span> : <span className="text-neutral-400">Unconfirmed</span>}
              {r.converted_to_patron_at && <span className="bg-blue-100 text-blue-800 px-2 py-0.5">Patron</span>}
              {r.notified_count > 0 && <span className="bg-amber-50 text-amber-800 px-2 py-0.5 border border-amber-300" data-testid={`notified-badge-${r.id}`}>Notified {r.notified_count}×</span>}
              <button
                onClick={() => deleteRow(r.id, r.email)}
                className="text-red-600 hover:text-red-800 ml-2 text-base"
                title="Delete signup"
                data-testid={`delete-${r.id}`}
              >×</button>
            </div>
          </div>
        );})}
        {!loading && rows.length === 0 && <div className="text-neutral-500 italic">{tab === "overseas" ? "No overseas signups yet. Once Cloudflare detects a non-PK IP, those signups land here automatically." : "No signups yet."}</div>}
      </div>

      {/* Draft modal */}
      {draft && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setDraft(null)} data-testid="notify-modal">
          <div className="bg-white max-w-3xl w-full p-6 border-2 border-black hard-shadow max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="overline mb-2">DRAFT — NOTIFY PATRONS · {draft.mode === "selected" ? `${selected.size} SELECTED` : "ALL ELIGIBLE"}</div>
            <p className="text-xs text-neutral-500 mb-4">References {draft.active_wave_count} active wave{draft.active_wave_count === 1 ? "" : "s"}. Edit subject + body, then send. <code>{"{{name}}"}</code> and <code>{"{{token}}"}</code> are auto-substituted per recipient.</p>
            <Label>Subject</Label>
            <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="mb-4" />
            <Label>Body (HTML)</Label>
            <textarea value={draft.body_html} onChange={(e) => setDraft({ ...draft, body_html: e.target.value })} rows={12} className="w-full border border-neutral-300 p-3 font-mono text-xs" />
            <div className="space-y-2 mt-3 text-sm">
              <div className="flex items-center gap-2">
                <Switch checked={onlyUnconverted} onCheckedChange={setOnlyUnconverted} data-testid="opt-only-unconverted" />
                <span>Only send to patrons who haven't joined yet (skip converted)</span>
              </div>
              {draft.mode === "all" && (
                <div className="flex items-center gap-2">
                  <Switch checked={includeUnconfirmed} onCheckedChange={setIncludeUnconfirmed} data-testid="opt-include-unconfirmed" />
                  <span>Include <b>unconfirmed</b> signups (haven't clicked confirmation email yet)</span>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
              <Button onClick={send} disabled={busy} className="bg-mango hover:bg-mango/90 text-white" data-testid="notify-send">{busy ? "Sending…" : "Send"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return <div className="bg-white border-2 border-black p-4"><div className="overline text-neutral-500">{label}</div><div className="text-3xl font-black mt-1">{value}</div></div>;
}

// =====================================================================
//                          PARTNERS  (lightweight directory)
// =====================================================================
// Vouchers used to live here too (System A) — retired in favour of the
// unified Rewards & Vouchers builder (Admin → Rewards), which can now
// optionally link a Reward to a Partner from this same list for
// logo/name display. This page is purely a contacts/CRM directory now.
export function AdminPartners() {
  const blankPForm = { name: "", contact_name: "", contact_email: "", phone: "", website: "", sector: "", logo_url: "", notes: "", active: true };
  const [partners, setPartners] = useState([]);
  const [pForm, setPForm] = useState(blankPForm);
  const [editingId, setEditingId] = useState(null); // partner.id when editing, null when adding new
  const [deleteTarget, setDeleteTarget] = useState(null); // partner being confirmed for deletion
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => { try { setPartners((await api.get("/admin/partners")).data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setEditingId(p.id);
    setPForm({ name: p.name || "", contact_name: p.contact_name || "", contact_email: p.contact_email || "", phone: p.phone || "", website: p.website || "", sector: p.sector || "", logo_url: p.logo_url || "", notes: p.notes || "", active: p.active !== false });
  };
  const cancelEdit = () => { setEditingId(null); setPForm(blankPForm); };

  const savePartner = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/partners/${editingId}`, pForm);
        toast.success("Partner updated");
      } else {
        await api.post("/admin/partners", pForm);
        toast.success("Partner added");
      }
      setEditingId(null);
      setPForm(blankPForm);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/partners/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} deleted`);
      if (editingId === deleteTarget.id) cancelEdit();
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-partners">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">SECTION · PARTNERS</div>
        <h1 className="text-4xl font-black tracking-tighter">Partner directory</h1>
        <p className="text-sm text-neutral-500 mt-1">Contact records for businesses sponsoring rewards. Link a Reward to a partner from <Link to="/admin/rewards" className="underline">Admin → Rewards & Vouchers</Link> for logo/name display.</p>
      </div>

      <form onSubmit={savePartner} className="bg-white border-2 border-black p-5 space-y-3 mb-8 max-w-lg" data-testid="partner-form">
        <div className="overline flex items-center justify-between">
          <span>{editingId ? "EDIT PARTNER" : "+ NEW PARTNER"}</span>
          {editingId && <button type="button" onClick={cancelEdit} className="text-neutral-400 font-normal underline text-xs" data-testid="partner-cancel-edit">Cancel</button>}
        </div>
        <Input placeholder="Company name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} required data-testid="partner-name-input" />
        <Input placeholder="Contact name" value={pForm.contact_name} onChange={(e) => setPForm({ ...pForm, contact_name: e.target.value })} />
        <Input placeholder="Contact email" value={pForm.contact_email} onChange={(e) => setPForm({ ...pForm, contact_email: e.target.value })} />
        <Input placeholder="Phone" value={pForm.phone} onChange={(e) => setPForm({ ...pForm, phone: e.target.value })} />
        <Input placeholder="Website" value={pForm.website} onChange={(e) => setPForm({ ...pForm, website: e.target.value })} />
        <Input placeholder="Sector (e.g. Retail)" value={pForm.sector} onChange={(e) => setPForm({ ...pForm, sector: e.target.value })} />
        <div>
          <Input placeholder="Logo URL" value={pForm.logo_url} onChange={(e) => setPForm({ ...pForm, logo_url: e.target.value })} data-testid="partner-logo-url-input" />
          <p className="text-xs text-neutral-400 mt-1" data-testid="partner-logo-size-hint">Recommended: square logo, 200×200px (transparent PNG works best) — this is the size that renders crisply as the small badge on the patron's Vouchers card and on the redemption receipt.</p>
        </div>
        <textarea placeholder="Notes" value={pForm.notes} onChange={(e) => setPForm({ ...pForm, notes: e.target.value })} className="w-full border border-neutral-300 p-2 text-sm" rows={3} />
        <Button type="submit" className="w-full bg-black hover:bg-neutral-800 rounded-none" data-testid="partner-save-btn">{editingId ? "Save changes" : "Add Partner"}</Button>
      </form>

      <div className="overline mb-2">PARTNERS ({partners.length})</div>
      <div className="space-y-2 mb-6">
        {partners.map((p) => (
          <div key={p.id} className="bg-white border border-neutral-200 p-3 flex justify-between items-center gap-3" data-testid={`partner-row-${p.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              {p.logo_url && <img src={p.logo_url} alt="" className="w-8 h-8 rounded-full object-contain border border-neutral-200 bg-white shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
              <div className="min-w-0">
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-neutral-500">{p.sector || "—"} · {p.contact_email || "no contact"}</div>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => startEdit(p)} data-testid={`partner-edit-${p.id}`}>Edit</Button>
              <Button variant="outline" size="sm" onClick={() => setDeleteTarget(p)} data-testid={`partner-delete-${p.id}`}>Delete</Button>
            </div>
          </div>
        ))}
        {!loading && partners.length === 0 && <div className="text-neutral-500 italic">No partners yet.</div>}
      </div>

      {/* In-app confirm dialog (NOT window.confirm — silently blocked inside
          the preview panel's iframe without allow="modals", making delete
          look "unresponsive" to a real admin; same fix pattern as ITER 109's
          Featured-swap dialog). */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="rounded-none border-2 border-black max-w-md" data-testid="partner-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black tracking-tighter">Delete {deleteTarget?.name}?</DialogTitle>
            <DialogDescription className="text-neutral-700 pt-1">
              Any Reward currently linked to this partner keeps working — it just loses its logo/name display. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteBusy} data-testid="partner-delete-cancel">Cancel</Button>
            <Button onClick={doDelete} disabled={deleteBusy} className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="partner-delete-confirm">{deleteBusy ? "Deleting…" : "Delete permanently"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =====================================================================
//                              RAFFLE
// =====================================================================
export function AdminRaffle() {
  const [sponsors, setSponsors] = useState([]);
  const [draws, setDraws] = useState([]);
  const [sForm, setSForm] = useState({ name: "", contact_name: "", contact_email: "", phone: "", website: "", logo_url: "", notes: "", active: true });
  const [dForm, setDForm] = useState({ period: "", threshold: 5000, draw_date: "", prize_label: "", prize_value_pkr: 0, sponsor_id: "", points_deduction: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, d] = await Promise.all([api.get("/admin/raffle/sponsors"), api.get("/admin/raffle/draws")]);
      setSponsors(s.data); setDraws(d.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveSponsor = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/raffle/sponsors", sForm); toast.success("Sponsor added"); setSForm({ name: "", contact_name: "", contact_email: "", phone: "", website: "", logo_url: "", notes: "", active: true }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const saveDraw = async (e) => {
    e.preventDefault();
    try {
      // points_deduction: blank input → null (no deduction, cooldown only).
      // Any non-empty numeric string is coerced to int and passed through.
      const rawPD = String(dForm.points_deduction ?? "").trim();
      const pointsDeduction = rawPD === "" ? null : Math.max(0, Math.floor(Number(rawPD) || 0));
      await api.post("/admin/raffle/draws", {
        ...dForm,
        threshold: Number(dForm.threshold),
        prize_value_pkr: Number(dForm.prize_value_pkr),
        sponsor_id: dForm.sponsor_id || null,
        points_deduction: pointsDeduction,
      });
      toast.success("Draw created"); setDForm({ ...dForm, period: "", draw_date: "", prize_label: "", prize_value_pkr: 0, points_deduction: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const pickWinner = async (did) => {
    try {
      const r = await api.post(`/admin/raffle/draws/${did}/pick-winner`);
      toast.success(`Winner: ${r.data.winner.name} (pool ${r.data.pool_size})`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const confirmWinner = async (did) => {
    const ev = window.prompt("Evidence URL (receipt / voucher screenshot)? Leave empty to skip.");
    try { await api.post(`/admin/raffle/draws/${did}/confirm`, { evidence_url: ev || "" }); toast.success("Confirmed + winner emailed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const cancelDraw = async (did) => {
    if (!window.confirm("Cancel this draw?")) return;
    try { await api.post(`/admin/raffle/draws/${did}/cancel`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-6xl" data-testid="admin-raffle">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">SECTION · RAFFLE</div>
        <h1 className="text-4xl font-black tracking-tighter">Raffle draws & sponsors</h1>
        <p className="text-sm text-neutral-500 mt-1">Only visible while <b>Incentive mode</b> = Raffle. Entries are weighted linearly: <b>1 + draws_missed_consecutive</b> per patron, capped by the admin-set max.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <form onSubmit={saveSponsor} className="bg-white border-2 border-black p-5 space-y-3" data-testid="sponsor-form">
          <div className="overline">+ NEW SPONSOR</div>
          <Input placeholder="Sponsor name" value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} required />
          <Input placeholder="Contact name" value={sForm.contact_name} onChange={(e) => setSForm({ ...sForm, contact_name: e.target.value })} />
          <Input placeholder="Contact email" value={sForm.contact_email} onChange={(e) => setSForm({ ...sForm, contact_email: e.target.value })} />
          <Input placeholder="Phone" value={sForm.phone} onChange={(e) => setSForm({ ...sForm, phone: e.target.value })} />
          <div>
            <Input placeholder="Logo URL" value={sForm.logo_url} onChange={(e) => setSForm({ ...sForm, logo_url: e.target.value })} />
            <p className="text-xs text-neutral-400 mt-1 flex items-center gap-1">Square, 200×200px recommended for future use <HelpHint text="Not yet displayed anywhere on the public site — only the sponsor's NAME currently appears in draw results and winner emails. Stored here for your records / ready for when a sponsor-logo display is built." /></p>
          </div>
          <Button type="submit" className="w-full bg-black hover:bg-neutral-800 rounded-none">Add Sponsor</Button>
        </form>

        <form onSubmit={saveDraw} className="bg-white border-2 border-black p-5 space-y-3" data-testid="draw-form">
          <div className="overline">+ NEW DRAW</div>
          <Input placeholder="Period (e.g. 'Mar 2026')" value={dForm.period} onChange={(e) => setDForm({ ...dForm, period: e.target.value })} required />
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Threshold (pts)</Label><Input type="number" value={dForm.threshold} onChange={(e) => setDForm({ ...dForm, threshold: e.target.value })} /></div>
            <div><Label className="text-xs">Prize value (PKR)</Label><Input type="number" value={dForm.prize_value_pkr} onChange={(e) => setDForm({ ...dForm, prize_value_pkr: e.target.value })} /></div>
          </div>
          <Label className="text-xs">Draw date</Label>
          <Input type="date" value={dForm.draw_date} onChange={(e) => setDForm({ ...dForm, draw_date: e.target.value })} required />
          <Input placeholder="Prize label (e.g. 'PKR 10,000 Daraz voucher')" value={dForm.prize_label} onChange={(e) => setDForm({ ...dForm, prize_label: e.target.value })} required />
          <div>
            <Label className="text-xs">Points to deduct from winner on confirm <span className="text-neutral-500 font-normal">(leave blank → no deduction; cooldown alone prevents repeats)</span></Label>
            <Input
              type="number"
              min="0"
              placeholder="e.g. 5000 (blank = 0)"
              value={dForm.points_deduction}
              onChange={(e) => setDForm({ ...dForm, points_deduction: e.target.value })}
              data-testid="draw-points-deduction"
            />
          </div>
          <select value={dForm.sponsor_id} onChange={(e) => setDForm({ ...dForm, sponsor_id: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 bg-white">
            <option value="">— oodel.-funded —</option>
            {sponsors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <Button type="submit" className="w-full bg-mango hover:bg-mango/90 text-white rounded-none">Create Draw</Button>
        </form>
      </div>

      <div className="overline mb-2">DRAWS ({draws.length})</div>
      <div className="space-y-3">
        {draws.map((d) => (
          <div key={d.id} className="bg-white border-2 border-neutral-200 p-4" data-testid={`draw-${d.id}`}>
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <div>
                <div className="text-lg font-bold">{d.period} <span className="text-sm text-neutral-500">· threshold {d.threshold} pts</span></div>
                <div className="text-sm text-neutral-700 mt-1">{d.prize_label} <span className="text-neutral-500">· PKR {d.prize_value_pkr}</span></div>
                <div className="text-xs text-neutral-500 mt-1">
                  Sponsor: {sponsors.find((s) => s.id === d.sponsor_id)?.name || "oodel."} · Status: <b className="uppercase">{d.status}</b>
                  {" · "}Winner deduction:{" "}
                  <b className={Number(d.points_deduction) > 0 ? "text-red-700" : "text-green-700"}>
                    {Number(d.points_deduction) > 0 ? `−${d.points_deduction} pts` : "none (cooldown only)"}
                  </b>
                </div>
                {d.winner_snapshot && (
                  <div className="text-xs text-green-700 mt-2 font-mono leading-relaxed bg-green-50 border border-green-200 p-2" data-testid={`winner-info-${d.id}`}>
                    <div className="font-bold text-green-800 uppercase tracking-wider text-[10px] mb-1">WINNER</div>
                    <div><b>Name:</b> {d.winner_snapshot.name}</div>
                    <div><b>Email:</b> {d.winner_snapshot.email || "—"}</div>
                    <div><b>Phone:</b> {d.winner_snapshot.phone || "—"}</div>
                    <div><b>City:</b> {d.winner_snapshot.city || "—"}</div>
                    <div><b>Entries held:</b> {d.winner_snapshot.entries_held}</div>
                  </div>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                {d.status === "open" && <Button size="sm" onClick={() => pickWinner(d.id)} className="bg-mango hover:bg-mango/90 text-white">Pick winner</Button>}
                {d.status === "drawn" && (
                  <Button size="sm" onClick={() => confirmWinner(d.id)} className="bg-green-600 hover:bg-green-700">Confirm winner</Button>
                )}
                {(d.status === "open" || d.status === "drawn") && <Button size="sm" variant="outline" onClick={() => cancelDraw(d.id)}>Cancel</Button>}
              </div>
            </div>
          </div>
        ))}
        {!loading && draws.length === 0 && <div className="text-neutral-500 italic">No draws yet.</div>}
      </div>
    </div>
  );
}

// =====================================================================
//                              PM INBOX
// =====================================================================
export function AdminInbox() {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("note");
  const [loading, setLoading] = useState(true);

  const loadThreads = () => api.get("/admin/inbox/threads").then((r) => setThreads(r.data)).finally(() => setLoading(false));
  useEffect(() => { loadThreads(); }, []);

  const openThread = async (cid) => {
    setActiveId(cid);
    const r = await api.get(`/admin/inbox/threads/${cid}`);
    setThread(r.data);
    loadThreads();
  };

  const addMessage = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    try {
      await api.post(`/admin/inbox/threads/${activeId}/messages`, { kind, body });
      setBody("");
      openThread(activeId);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-7xl" data-testid="admin-inbox">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">SECTION · PM INBOX</div>
        <h1 className="text-4xl font-black tracking-tighter">Customer conversations</h1>
        <p className="text-sm text-neutral-500 mt-1">Per-customer thread log. Add notes, log calls, paste emails. Customer contact-form submissions land here automatically.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[600px]">
        <div className="md:col-span-1 border border-neutral-200 bg-white overflow-auto">
          {threads.map((t) => (
            <button key={t.customer_id} onClick={() => openThread(t.customer_id)}
              className={`w-full text-left p-4 border-b border-neutral-100 hover:bg-neutral-50 ${activeId === t.customer_id ? "bg-mango/10" : ""}`}
              data-testid={`inbox-thread-${t.client_id}`}>
              <div className="flex justify-between items-baseline gap-2">
                <div className="font-medium truncate">{t.company}</div>
                {t.unread_count > 0 && <span className="bg-mango text-white text-xs px-1.5 py-0.5">{t.unread_count}</span>}
              </div>
              <div className="text-xs text-neutral-500 truncate">{t.contact_name} · {t.tier}</div>
              <div className="text-xs text-neutral-400 truncate mt-1">{t.last_message_preview || "No messages yet"}</div>
            </button>
          ))}
          {!loading && threads.length === 0 && <div className="p-4 text-neutral-500 italic">No clients yet.</div>}
        </div>
        <div className="md:col-span-2 border border-neutral-200 bg-white overflow-hidden flex flex-col">
          {!thread && <div className="p-12 text-neutral-500 italic text-center">Pick a customer to view the conversation.</div>}
          {thread && (
            <>
              <div className="p-4 border-b border-neutral-200 bg-neutral-50">
                <div className="font-bold">{thread.customer.company}</div>
                <div className="text-xs text-neutral-500">{thread.customer.client_id} · {thread.customer.tier} · {thread.customer.name}</div>
              </div>
              <div className="flex-1 overflow-auto p-4 space-y-3" data-testid="inbox-messages">
                {thread.messages.map((m) => (
                  <div key={m.id} className={`p-3 border ${m.author_role === "customer" ? "bg-mango/5 border-mango/30" : "bg-white border-neutral-200"}`}>
                    <div className="flex justify-between items-baseline text-xs text-neutral-500 mb-1">
                      <span><b>{m.author_name}</b> · {m.kind} · {m.author_role}</span>
                      <span>{m.created_at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    {m.subject && <div className="font-semibold text-sm">{m.subject}</div>}
                    <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                  </div>
                ))}
                {thread.messages.length === 0 && <div className="text-neutral-400 italic">No messages — be the first to log a note.</div>}
              </div>
              <form onSubmit={addMessage} className="p-3 border-t border-neutral-200 bg-neutral-50">
                <div className="flex gap-2 mb-2">
                  <select value={kind} onChange={(e) => setKind(e.target.value)} className="border border-neutral-300 px-2 py-1 text-sm bg-white">
                    <option value="note">Note</option>
                    <option value="call">Call</option>
                    <option value="meeting">Meeting</option>
                    <option value="email_out">Email sent</option>
                    <option value="email_in">Email received</option>
                    <option value="internal">Internal</option>
                  </select>
                  <span className="text-xs text-neutral-500 self-center">Log anything customer-related — calls, meetings, emails, internal notes.</span>
                </div>
                <div className="flex gap-2">
                  <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a note…" className="flex-1 border border-neutral-300 p-2 text-sm" rows={2} data-testid="inbox-body" />
                  <Button type="submit" className="bg-black hover:bg-neutral-800 rounded-none" data-testid="inbox-send">Add</Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
